import { Container, Graphics, Sprite, type FederatedPointerEvent } from 'pixi.js';
import gsap from 'gsap';
import { BOARD, COLORS, SHAPE, TIMING } from '../config/tokens';
import type { Game, Step } from '../core/game';
import type { Piece, Pos, Special } from '../core/types';
import { frame } from '../assets/loader';
import { FRAMES, tileFrame } from '../assets/names';
import { cascadePitch, sound } from '../audio/sound';
import { haptic } from '../audio/haptics';
import { Fx, KIND_COLOR, reducedMotion } from './fx';
import { after } from '../ui/components';

const OVERLAY_FRAME: Partial<Record<Special, string>> = {
  stripeH: FRAMES.stripeH,
  stripeV: FRAMES.stripeV,
  bomb: FRAMES.bombRing,
};
const SWIPE_MIN = 28; // reference px before a drag counts as a swipe
const HINT_AFTER = 6000; // ms without input before a move is suggested
const RIPPLE = 0.028; // s per cell distance: blasts spread outwards
const sec = (ms: number) => ms / 1000;
const key = (p: Pos) => `${p.row},${p.col}`;
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
/** stops every running tween on a piece before it is destroyed */
const dispose = (c: Container) => {
  gsap.killTweensOf(c);
  gsap.killTweensOf(c.scale);
  gsap.killTweensOf(c.position);
  for (const ch of c.children) gsap.killTweensOf(ch);
  c.destroy({ children: true });
};

/**
 * Draws the board from the game state and plays back the steps the logic
 * returns. The logic is finished before the animation starts, so pieces are
 * followed by id and the board is never read mid-turn.
 * Frame and cells are drawn in code (sharp, no dark grid, §6); tiles, mud
 * and stains are atlas sprites. Layer order follows §14.
 */
export class BoardView {
  readonly view = new Container();
  private readonly stains = new Container();
  private readonly pieces = new Container();
  private readonly sprites = new Map<number, Container>();
  private readonly mud = new Map<string, Sprite>();
  private readonly stainSprites = new Map<string, Container>();
  private readonly ox = BOARD.inner.x - BOARD.frame.x;
  private readonly oy = BOARD.inner.y - BOARD.frame.y;
  private busy = false;
  private dragStart?: { pos: Pos; x: number; y: number };
  private selected?: Pos;
  private hintTimer?: ReturnType<typeof setTimeout>;
  private hintTween?: gsap.core.Timeline;
  private hintRestore?: () => void;

  /** called after every finished action (HUD refresh, win/lose) */
  onChange: () => void = () => undefined;
  /** called when a step starts playing (HUD effects: goal flights, praise, dog) */
  onStep: (step: Step) => void = () => undefined;
  /** called when a cell is tapped while a board-targeting booster is armed */
  onCellTap?: (p: Pos) => void;
  /** tutorial: only swaps this returns true for are accepted */
  allowSwap?: (a: Pos, b: Pos) => boolean;
  /** idle move hint (off during the tutorial, which has its own pointer) */
  hints = true;

  constructor(private game: Game, private readonly fx: Fx) {
    const { frame: f, inner, cell } = BOARD;
    const { ox, oy } = this;
    const bg = new Graphics()
      .roundRect(0, 6, f.w, f.h, 36).fill(COLORS.cocoa)
      .roundRect(0, 0, f.w, f.h, 36).fill(COLORS.wood).stroke({ width: SHAPE.outline, color: COLORS.cocoa })
      .roundRect(ox - 6, oy - 6, inner.w + 12, inner.h + 12, 14).fill(COLORS.cream).stroke({ width: SHAPE.outline, color: COLORS.cocoa });
    const cells = new Graphics();
    const b = game.board;
    for (let r = 0; r < b.rows; r++) {
      for (let c = 0; c < b.cols; c++) {
        if (!b.get(r, c).hole) cells.rect(ox + c * cell, oy + r * cell, cell, cell).fill((r + c) % 2 ? COLORS.latte : COLORS.cream);
      }
    }
    // subtle separators (§6: no dominant dark grid)
    for (let i = 1; i < BOARD.cols; i++) {
      cells.moveTo(ox + i * cell, oy).lineTo(ox + i * cell, oy + inner.h);
      cells.moveTo(ox, oy + i * cell).lineTo(ox + inner.w, oy + i * cell);
    }
    cells.stroke({ width: 1.5, color: COLORS.latte, alpha: 0.9 });

    const layer = new Container();
    layer.position.set(ox, oy);
    layer.addChild(this.stains, this.pieces);
    // new pieces drop in from above: hide them until they enter the board
    const mask = new Graphics().rect(ox, oy - 4, inner.w, inner.h + 8).fill(0xffffff);
    layer.mask = mask;
    this.view.addChild(bg, cells, layer, mask);

    layer.eventMode = 'static';
    layer.hitArea = { contains: (x: number, y: number) => x >= 0 && y >= 0 && x < inner.w && y < inner.h };
    layer.on('pointerdown', (e) => this.down(e, layer));
    layer.on('globalpointermove', (e) => this.move(e, layer));
    layer.on('pointerup', () => (this.dragStart = undefined));
    layer.on('pointerupoutside', () => (this.dragStart = undefined));

    this.rebuild();
    this.scheduleHint();
  }

  get isBusy(): boolean {
    return this.busy;
  }

  /** Swaps in a new game (restart) and recreates all sprites. */
  setGame(game: Game): void {
    this.game = game;
    this.selected = undefined;
    this.rebuild();
    this.scheduleHint();
  }

  destroy(): void {
    clearTimeout(this.hintTimer);
  }

  /** Centre of a cell in the coordinate space of the board's parent (= fx layer). */
  cellPoint(p: Pos): { x: number; y: number } {
    const c = this.center(p);
    return { x: this.view.x - this.view.pivot.x + this.ox + c.x, y: this.view.y - this.view.pivot.y + this.oy + c.y };
  }

  /** Test helper: pieces whose sprite is missing or not on its cell (should be 0). */
  mismatches(details?: string[]): number {
    // the idle hint wiggles two pieces on purpose: measure without it
    if (this.hintTween) this.scheduleHint();
    let bad = 0;
    const b = this.game.board;
    for (const p of b.positions()) {
      const piece = b.at(p).piece;
      if (!piece) continue;
      const s = this.sprites.get(piece.id);
      const c = this.center(p);
      if (!s || s.destroyed || Math.abs(s.x - c.x) > 1 || Math.abs(s.y - c.y) > 1) {
        bad++;
        details?.push(`${p.row},${p.col} id${piece.id}: ${s ? `${s.x - c.x},${s.y - c.y}${s.destroyed ? ' destroyed' : ''}` : 'no sprite'}`);
      }
    }
    return bad + Math.abs(this.sprites.size - b.count((c) => !!c.piece));
  }

  // ------------------------------------------------------------ sprites

  private rebuild(): void {
    this.pieces.removeChildren().forEach(dispose);
    this.stains.removeChildren().forEach((c) => c.destroy());
    this.sprites.clear();
    this.mud.clear();
    this.stainSprites.clear();
    const b = this.game.board;
    for (const p of b.positions()) {
      const cell = b.at(p);
      if (cell.stain) this.addStain(p);
      if (cell.blocker) this.addMud(p, cell.blocker.hp);
      if (cell.piece) this.addPiece(cell.piece, p);
    }
  }

  private center(p: Pos) {
    return { x: p.col * BOARD.cell + BOARD.cell / 2, y: p.row * BOARD.cell + BOARD.cell / 2 };
  }

  private fitted(name: string, scale: number): Sprite {
    const s = new Sprite(frame(name));
    s.anchor.set(0.5);
    s.scale.set((BOARD.cell * scale) / Math.max(s.texture.width, s.texture.height));
    return s;
  }

  private addPiece(piece: Piece, p: Pos): Container {
    const c = new Container();
    c.addChild(this.fitted(piece.special === 'colorstar' ? FRAMES.colorstar : tileFrame(piece.kind!), BOARD.tileScale));
    const overlay = piece.special && OVERLAY_FRAME[piece.special];
    if (overlay) c.addChild(this.fitted(overlay, 0.92));
    if (piece.special === 'colorstar') gsap.to(c.children[0], { rotation: 0.12, duration: 1.2, yoyo: true, repeat: -1, ease: 'sine.inOut' });
    c.position.set(this.center(p).x, this.center(p).y);
    this.pieces.addChild(c);
    this.sprites.set(piece.id, c);
    return c;
  }

  private addMud(p: Pos, hp: number): Sprite {
    const s = this.fitted(hp >= 2 ? FRAMES.mud2 : FRAMES.mud1, 0.9);
    s.position.set(this.center(p).x, this.center(p).y);
    this.pieces.addChild(s);
    this.mud.set(key(p), s);
    return s;
  }

  /** Paw stain: darker cell with a big paw print, visible around the tile on top. */
  private addStain(p: Pos): void {
    const c = this.center(p);
    const stain = new Container();
    const bg = new Graphics().roundRect(-BOARD.cell / 2 + 3, -BOARD.cell / 2 + 3, BOARD.cell - 6, BOARD.cell - 6, 14).fill({ color: COLORS.wood, alpha: 0.35 });
    const paw = this.fitted(FRAMES.pawStain, 0.98);
    paw.alpha = 0.5;
    stain.addChild(bg, paw);
    stain.position.set(c.x, c.y);
    this.stains.addChild(stain);
    this.stainSprites.set(key(p), stain);
  }

  private spriteAt(p: Pos): Container | undefined {
    const piece = this.game.board.inside(p) ? this.game.board.at(p).piece : null;
    return piece ? this.sprites.get(piece.id) : undefined;
  }

  // ------------------------------------------------------------ input

  private cellAt(e: FederatedPointerEvent, layer: Container): Pos | null {
    const l = layer.toLocal(e.global);
    const p = { row: Math.floor(l.y / BOARD.cell), col: Math.floor(l.x / BOARD.cell) };
    return this.game.board.inside(p) ? p : null;
  }

  private down(e: FederatedPointerEvent, layer: Container): void {
    sound.unlock();
    this.stopHint();
    if (this.busy || this.game.status !== 'playing') return;
    const p = this.cellAt(e, layer);
    if (!p) return;
    if (this.onCellTap) {
      this.onCellTap(p);
      return;
    }
    // tap-tap: a second tap on a neighbour swaps
    if (this.selected && Math.abs(this.selected.row - p.row) + Math.abs(this.selected.col - p.col) === 1) {
      const from = this.selected;
      this.select(undefined);
      this.trySwap(from, p);
      return;
    }
    this.select(p);
    const l = layer.toLocal(e.global);
    this.dragStart = { pos: p, x: l.x, y: l.y };
  }

  private move(e: FederatedPointerEvent, layer: Container): void {
    if (!this.dragStart || this.busy) return;
    const l = layer.toLocal(e.global);
    const dx = l.x - this.dragStart.x;
    const dy = l.y - this.dragStart.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_MIN) return;
    const from = this.dragStart.pos;
    const to = Math.abs(dx) > Math.abs(dy) ? { row: from.row, col: from.col + Math.sign(dx) } : { row: from.row + Math.sign(dy), col: from.col };
    this.dragStart = undefined;
    this.select(undefined);
    if (this.game.board.inside(to)) this.trySwap(from, to);
  }

  private trySwap(a: Pos, b: Pos): void {
    if (this.allowSwap && !this.allowSwap(a, b)) {
      sound.play('invalid');
      return;
    }
    void this.run(() => this.game.swap(a, b).steps);
  }

  private select(p: Pos | undefined): void {
    const old = this.selected && this.spriteAt(this.selected);
    if (old) gsap.to(old.scale, { x: 1, y: 1, duration: 0.12 });
    this.selected = p;
    const now = p && this.spriteAt(p);
    if (now) {
      sound.play('tap');
      gsap.timeline()
        .to(now.scale, { x: TIMING.tileSelectScale * 1.06, y: TIMING.tileSelectScale * 0.94, duration: 0.07 })
        .to(now.scale, { x: TIMING.tileSelectScale, y: TIMING.tileSelectScale, duration: 0.25, ease: 'elastic.out(1, 0.5)' });
    }
  }

  // ------------------------------------------------------------ hint

  private scheduleHint(): void {
    this.stopHint();
    this.hintTimer = setTimeout(() => this.showHint(), HINT_AFTER);
  }

  private stopHint(): void {
    clearTimeout(this.hintTimer);
    this.hintTween?.kill();
    this.hintRestore?.();
    this.hintTween = undefined;
    this.hintRestore = undefined;
  }

  private showHint(): void {
    if (!this.hints || this.view.destroyed) return;
    if (this.busy || this.game.status !== 'playing' || this.onCellTap) return this.scheduleHint();
    const move = this.game.validMoves()[0];
    const [a, b] = move ? [this.spriteAt(move.a), this.spriteAt(move.b)] : [];
    if (!a || !b) return;
    const dx = (move.b.col - move.a.col) * 7;
    const dy = (move.b.row - move.a.row) * 7;
    const pa = { x: a.x, y: a.y };
    const pb = { x: b.x, y: b.y };
    this.hintRestore = () => {
      if (!a.destroyed) a.position.set(pa.x, pa.y);
      if (!b.destroyed) b.position.set(pb.x, pb.y);
    };
    this.hintTween = gsap.timeline({ repeat: -1, repeatDelay: 1.6 })
      .to(a, { x: pa.x + dx, y: pa.y + dy, duration: 0.18, ease: 'sine.inOut', yoyo: true, repeat: 3 })
      .to(b, { x: pb.x - dx, y: pb.y - dy, duration: 0.18, ease: 'sine.inOut', yoyo: true, repeat: 3 }, '<');
  }

  // ------------------------------------------------------------ playback

  /** Runs a logic action and animates its steps; input is locked meanwhile. */
  async run(action: () => Step[]): Promise<void> {
    if (this.busy) return;
    this.stopHint();
    const steps = action();
    if (!steps.length) return;
    this.busy = true;
    for (const step of steps) {
      this.onStep(step);
      await this.animate(step);
    }
    this.busy = false;
    this.onChange();
    this.scheduleHint();
  }

  private tween(target: object, vars: gsap.TweenVars): Promise<void> {
    return new Promise((resolve) => gsap.to(target, { ...vars, onComplete: () => resolve() }));
  }

  private async animate(step: Step): Promise<void> {
    switch (step.type) {
      case 'swap':
      case 'swapBack':
        return this.animateSwap(step);
      case 'transform':
        sound.play('create', { pitch: 1.2 });
        for (const [i, t] of step.pieces.entries()) {
          after(this.view, i * 60, () => {
            this.replace(t.piece, t.pos);
            this.fx.burst(this.cellPoint(t.pos).x, this.cellPoint(t.pos).y, { count: 5, frame: 'vfx_star', size: 26, gravity: 0 });
          });
        }
        await wait(step.pieces.length * 60 + 250);
        return;
      case 'clear':
        return this.animateClear(step);
      case 'fall':
        return this.animateFall(step);
      case 'shuffle':
        sound.play('shuffle');
        for (const r of step.replaced) this.replace(r.piece, r.pos);
        await Promise.all(step.moves.map((m) => {
          const s = this.sprites.get(m.id);
          if (!s) return Promise.resolve();
          gsap.fromTo(s, { rotation: 0 }, { rotation: Math.PI * 2, duration: 0.6, ease: 'power2.inOut' });
          return this.tween(s.position, { ...this.center(m.to), duration: 0.6, ease: 'back.inOut(1.4)' });
        }));
        return;
      case 'end':
        return;
    }
  }

  private async animateSwap(step: Step & { type: 'swap' | 'swapBack' }): Promise<void> {
    const [sa, sb] = step.ids.map((id) => this.sprites.get(id));
    const back = step.type === 'swapBack';
    // swap: piece A goes a → b; swapBack: it returns b → a
    const aTo = this.center(back ? step.a : step.b);
    const bTo = this.center(back ? step.b : step.a);
    const horizontal = step.a.row === step.b.row;
    const stretch = horizontal ? { x: 1.14, y: 0.88 } : { x: 0.88, y: 1.14 };
    const d = sec(TIMING.swap);
    if (back) {
      sound.play('invalid');
      haptic('warning');
    } else sound.play('swap');
    for (const s of [sa, sb]) {
      if (!s) continue;
      gsap.timeline()
        .to(s.scale, { ...stretch, duration: d / 2, ease: 'power1.out' })
        .to(s.scale, { x: 1, y: 1, duration: 0.3, ease: 'elastic.out(1, 0.45)' });
    }
    await Promise.all([
      sa && this.tween(sa.position, { ...aTo, duration: d, ease: back ? 'back.out(2)' : 'power2.inOut' }),
      sb && this.tween(sb.position, { ...bTo, duration: d, ease: back ? 'back.out(2)' : 'power2.inOut' }),
    ]);
    if (back && sa) gsap.fromTo(sa, { rotation: -0.12 }, { rotation: 0, duration: 0.35, ease: 'elastic.out(1, 0.3)' });
  }

  private async animateClear(step: Step & { type: 'clear' }): Promise<void> {
    const cellFx = (p: Pos) => this.cellPoint(p);
    const many = step.cleared.length > 16;

    // --- activations: beams, shockwaves, star lines; the blast spreads as a ripple
    const delayOf = new Map<string, number>();
    for (const a of step.activated) {
      const c = cellFx(a.pos);
      const sp = a.piece.special;
      if (sp === 'stripeH' || sp === 'stripeV') {
        const horizontal = sp === 'stripeH';
        const line = this.cellPoint(horizontal ? { row: a.pos.row, col: 3.5 } : { row: 3.5, col: a.pos.col });
        this.fx.beam(line.x, line.y, horizontal, BOARD.inner.w, 58, KIND_COLOR[a.piece.kind!]);
        sound.play('stripe');
        haptic('medium');
      } else if (sp === 'bomb') {
        this.fx.shockwave(c.x, c.y, 200, KIND_COLOR[a.piece.kind!]);
        this.fx.burst(c.x, c.y, { frame: 'vfx_bean', count: 8, speed: 520, size: 30 });
        this.fx.burst(c.x, c.y, { frame: 'vfx_steam', count: 4, speed: 120, gravity: -200, size: 70, life: 0.7 });
        sound.play('bomb');
        haptic('heavy');
        this.shake(10);
      } else if (sp === 'colorstar') {
        this.fx.shockwave(c.x, c.y, 260, COLORS.butter, 450);
        const sparkle = frame('vfx_star');
        a.area.slice(0, 18).forEach((p, i) => this.fx.fly(sparkle, c, cellFx(p), 30, i * 0.015, () => undefined));
        sound.play('colorstar');
        haptic('heavy');
      }
      for (const p of a.area) {
        const d = Math.hypot(p.row - a.pos.row, p.col - a.pos.col) * RIPPLE + (sp === 'colorstar' ? 0.3 : 0);
        const k = key(p);
        delayOf.set(k, Math.min(delayOf.get(k) ?? Infinity, d));
      }
    }
    if (step.combo) {
      const centre = step.cleared[0] ? cellFx(step.cleared[0].pos) : cellFx({ row: 3.5, col: 3.5 });
      this.fx.shockwave(centre.x, centre.y, 420, COLORS.raspberry, 500);
      this.fx.confettiBurst(centre.x, centre.y, 30);
      sound.play('combo');
      haptic('heavy');
      this.shake(16);
    }

    // --- pieces pop (squash, then shrink), with bubbles in the tile's colour
    if (step.activated.length === 0 && !step.combo) {
      sound.play('pop', { pitch: cascadePitch(step.cascade) });
      haptic(step.cascade > 1 ? 'medium' : 'light');
    } else setTimeout(() => sound.play('pop', { pitch: cascadePitch(step.cascade) * 1.1, volume: 0.7 }), 90);
    let longest = sec(TIMING.matchPop);
    const pops = step.cleared.map(({ piece, pos }) => {
      const s = this.sprites.get(piece.id);
      this.sprites.delete(piece.id);
      if (!s) return Promise.resolve();
      gsap.killTweensOf(s.scale);
      const delay = delayOf.get(key(pos)) ?? 0;
      longest = Math.max(longest, delay + sec(TIMING.matchPop));
      const p = cellFx(pos);
      return new Promise<void>((resolve) => {
        gsap.timeline({ delay, onComplete: () => (dispose(s), resolve()) })
          .to(s.scale, { x: 1.28, y: 0.8, duration: sec(TIMING.matchPop) * 0.35, ease: 'power2.out' })
          .call(() => {
            const tint = piece.kind ? KIND_COLOR[piece.kind] : COLORS.butter;
            this.fx.burst(p.x, p.y, { frame: 'vfx_bubble', tint, count: many ? 2 : 4, speed: 260, gravity: -120, size: 26, life: 0.5 });
            if (!many) this.fx.burst(p.x, p.y, { frame: 'vfx_sparkle', count: 2, speed: 180, gravity: 0, size: 22, life: 0.35 });
          })
          .to(s.scale, { x: 0, y: 0, duration: sec(TIMING.matchPop) * 0.65, ease: 'back.in(2)' })
          .to(s, { rotation: (Math.random() - 0.5) * 0.8, alpha: 0.4, duration: sec(TIMING.matchPop) * 0.65 }, '<');
      });
    });

    // --- mud and stains
    for (const h of step.mudHits) {
      const p = cellFx(h.pos);
      const old = this.mud.get(key(h.pos));
      this.mud.delete(key(h.pos));
      const delay = (delayOf.get(key(h.pos)) ?? 0) * 1000;
      after(this.view, delay, () => {
        sound.play('mud');
        haptic('medium');
        this.fx.burst(p.x, p.y, { frame: 'vfx_bubble', tint: COLORS.wood, count: 7, speed: 380, gravity: 900, size: 22 });
        if (old) {
          gsap.killTweensOf(old.scale);
          const sc = old.scale.x;
          gsap.to(old.scale, { x: sc * 1.25, y: sc * 0.6, duration: 0.1, onComplete: () => old.destroy() });
        }
        if (h.hpLeft > 0) {
          const m = this.addMud(h.pos, h.hpLeft);
          const sc = m.scale.x;
          m.scale.set(sc * 1.2, sc * 0.7);
          gsap.to(m.scale, { x: sc, y: sc, duration: 0.45, ease: 'elastic.out(1, 0.4)' });
        } else this.fx.shockwave(p.x, p.y, 90, COLORS.wood);
      });
    }
    for (const pos of step.stainsCleared) {
      const s = this.stainSprites.get(key(pos));
      this.stainSprites.delete(key(pos));
      const p = cellFx(pos);
      setTimeout(() => sound.play('stain'), 60);
      this.fx.burst(p.x, p.y, { frame: 'vfx_bubble', tint: COLORS.sky, count: 6, speed: 160, gravity: -260, size: 30, life: 0.8 });
      if (s) gsap.to(s, { alpha: 0, duration: 0.35, onComplete: () => s.destroy({ children: true }) });
    }

    await Promise.all(pops);
    await wait(Math.max(0, longest * 1000 - sec(TIMING.matchPop) * 1000));

    // --- new specials appear where the match happened
    for (const c of step.created) {
      const s = this.addPiece(c.piece, c.pos);
      const p = cellFx(c.pos);
      s.scale.set(0.2);
      gsap.to(s.scale, { x: 1, y: 1, duration: 0.45, ease: 'elastic.out(1, 0.5)' });
      this.fx.shockwave(p.x, p.y, 110, COLORS.butter);
      this.fx.burst(p.x, p.y, { frame: 'vfx_sparkle', count: 6, speed: 260, gravity: 0, size: 30 });
      sound.play('create');
      haptic('medium');
    }
    if (step.created.length) await wait(180);
  }

  private async animateFall(step: Step & { type: 'fall' }): Promise<void> {
    const drops: Promise<void>[] = [];
    let landed = false;
    const land = (s: Container) => {
      if (!landed || Math.random() < 0.35) sound.play('drop', { volume: landed ? 0.5 : 1 });
      landed = true;
      gsap.timeline()
        .to(s.scale, { x: 1.16, y: 0.84, duration: 0.07, ease: 'power1.out' })
        .to(s.scale, { x: 1, y: 1, duration: 0.4, ease: 'elastic.out(1, 0.4)' });
    };
    const fall = (s: Container, to: Pos, rows: number, delay: number) => {
      const d = 0.1 + 0.06 * Math.sqrt(Math.max(1, rows)) * 1.6;
      gsap.to(s.scale, { x: 0.93, y: 1.09, duration: d * 0.6, delay });
      return new Promise<void>((resolve) =>
        gsap.to(s.position, { ...this.center(to), duration: d, delay, ease: 'power2.in', onComplete: () => (land(s), resolve()) }),
      );
    };
    for (const m of step.moves) {
      const s = this.sprites.get(m.id);
      if (s) drops.push(fall(s, m.to, m.to.row - m.from.row, m.to.col * 0.012));
    }
    const perCol = new Map<number, number>();
    for (const sp of step.spawns) perCol.set(sp.pos.col, (perCol.get(sp.pos.col) ?? 0) + 1);
    for (const sp of step.spawns) {
      const s = this.addPiece(sp.piece, sp.pos);
      const n = perCol.get(sp.pos.col)!;
      s.y -= n * BOARD.cell;
      drops.push(fall(s, sp.pos, n, sp.pos.col * 0.012 + 0.03));
    }
    await Promise.all(drops);
  }

  /** Small board shake for big moments (skipped with reduced motion). */
  private shake(strength: number): void {
    if (reducedMotion) return;
    gsap.killTweensOf(this.view.pivot);
    const tl = gsap.timeline({ onComplete: () => this.view.pivot.set(0, 0) });
    for (let i = 0; i < 5; i++) {
      const f = strength * (1 - i / 5);
      tl.to(this.view.pivot, { x: (Math.random() - 0.5) * 2 * f, y: (Math.random() - 0.5) * 2 * f, duration: 0.035 });
    }
  }

  private replace(piece: Piece, p: Pos): void {
    const old = this.sprites.get(piece.id);
    if (old) dispose(old);
    this.sprites.delete(piece.id);
    const s = this.addPiece(piece, p);
    s.scale.set(0.5);
    gsap.to(s.scale, { x: 1, y: 1, duration: 0.35, ease: 'elastic.out(1, 0.5)' });
  }
}
